import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Input, Radio, Spin, Divider, message } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import toast from 'react-hot-toast';
import {HiCreditCard} from "react-icons/hi2";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";

const statusTones = {
    new: 'blue',
    queue: 'amber',
    pending: 'orange',
    examined: 'green',
    completed: 'slate',
    canceled: 'red',
};

const NewAdmissions = () => {
    const { t } = useTranslation();
    const sum = (value) => `${value} ${t('common.currency')}`;
    const [admissions, setAdmissions] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [sorter, setSorter] = useState({ field: 'id_visit', order: 'descend' });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentType, setPaymentType] = useState('cash');
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [paymentReceipt, setPaymentReceipt] = useState(null);

    const fetchAdmissions = async (page = 1, pageSize = 10, sortField = 'id_visit', sortOrder = 'descend') => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/visit?page=${page}&size=${pageSize}&status[0]=examined`);
            const admissionsData = response.data.data
                .filter(admission => admission.bill === 'payed' || admission.bill === 'pending')
                .map((item, index) => {
                    const orders = item.orders.filter(order => order.service_type === false);
                    const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.amount), 0);
                    const totalPayed = item.transaction.reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
                    const totalDebit = totalAmount - totalPayed;

                    return {
                        key: index + 1,
                        id_visit: item.id,
                        patient_name: item.patient_id.name,
                        doctor_name: item.doctor.name,
                        total_amount: item.children_amount,
                        total_payed: item.children_payed,
                        total_debit: item.children_debit,
                        date_at: item.date_at,
                        status: item.status,
                        bill: item.bill,
                        orders: orders,
                        services: orders.map(order => order.service.name).join(', '),
                    };
                });

            const total = response.data.meta.total;

            const sortedData = admissionsData.sort((a, b) => {
                if (sortField && sortOrder) {
                    const orderMultiplier = sortOrder === 'ascend' ? 1 : -1;
                    if (sortField === 'id_visit' || sortField === 'key') {
                        return (a[sortField] - b[sortField]) * orderMultiplier;
                    }
                    return a[sortField].localeCompare(b[sortField]) * orderMultiplier;
                }
                return 0;
            });

            setAdmissions(sortedData);
            setPagination({ ...pagination, current: page, pageSize, total });
        } catch (error) {
            console.error("Error fetching admissions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmissions();
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        fetchAdmissions(pagination.current, pagination.pageSize, sorter.field, sorter.order);
        setSorter(sorter);
    };

    const filteredData = admissions.filter(admission =>
        admission.patient_name && admission.patient_name.toLowerCase().includes(searchText.toLowerCase())
    );

    const getStatusName = (status) => t(`status.${status}`, { defaultValue: status });

    const paymentTypeNames = {
        cash: t('payments.cash'),
        credit: t('payments.credit'),
        card: t('payments.creditCard')
    };

    const paymentBillNames = {
        pending: t('status.pending'),
        payed: t('status.payed'),
    };

    const handlePaymentClick = (record) => {
        setSelectedVisit(record);
        setIsModalVisible(true);
    };

    const payForServices = async (orderId, amount, paymentType) => {
        try {
            const response = await axiosInstance.post(`/visit/pay/${orderId}`, {
                amount: parseInt(amount),
                type: paymentType
            });
            return response.data;
        } catch (error) {
            console.error("Ошибка при оплате сервисов:", error);
            throw error;
        }
    };

    const handlePayment = () => {
        if (selectedVisit) {
            const firstOrder = selectedVisit.orders[0];
            if (!firstOrder) {
                console.error('Нет доступного ID ордера для оплаты');
                return;
            }

            const orderId = firstOrder.id;
            const amount = paymentAmount;
            payForServices(orderId, amount, paymentType)
                .then(response => {
                    console.log('Оплата выполнена успешно:', response);
                    fetchAdmissions();
                    setPaymentReceipt(response.data);
                    setPaymentAmount('');
                    setPaymentType('cash');
                    message.success(t('payments.paidLabel', { id: orderId }));
                })
                .catch(error => {
                    console.error('Ошибка при выполнении оплаты:', error);
                });
        } else {
            console.error('Выбранный заказ отсутствует для оплаты');
        }
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedVisit(null);
        setPaymentReceipt(null);
    };

    const handleSearch = (value) => {
        setSearchText(value);
    };

    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'key',
            key: 'id',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            sortOrder: sorter.field === 'key' && sorter.order,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('admissions.visitId'),
            dataIndex: 'id_visit',
            key: 'id_visit',
            width: 100,
            sorter: (a, b) => a.id_visit - b.id_visit,
            sortOrder: sorter.field === 'id_visit' && sorter.order,
            defaultSortOrder: sorter.field === 'id_visit' ? sorter.order : 'descend',
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: 'patient_name',
            key: 'patient_name',
            sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
            sortOrder: sorter.field === 'patient_name' && sorter.order,
            render: (text) => (
                <span className="block text-sm font-medium text-[#1D2C7B] min-w-[150px]">{text}</span>
            ),
        },
        {
            title: t('common.doctor'),
            dataIndex: 'doctor_name',
            key: 'doctor_name',
            sorter: (a, b) => a.doctor_name.localeCompare(b.doctor_name),
            sortOrder: sorter.field === 'doctor_name' && sorter.order,
            render: (text) => <span className="text-sm text-slate-700">{text}</span>,
        },

        {
            title: t('nav.services'),
            dataIndex: 'services',
            key: 'services',
            ellipsis: true,
            render: (text) => <span className="text-sm text-slate-600">{text || t('admissions.noServices')}</span>
        },
        {
            title: t('payments.total'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 140,
            sorter: (a, b) => a.total_amount - b.total_amount,
            sortOrder: sorter.field === 'total_amount' && sorter.order,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            )
        },
        {
            title: t('payments.paid'),
            dataIndex: 'total_payed',
            key: 'total_payed',
            width: 140,
            sorter: (a, b) => a.total_payed - b.total_payed,
            sortOrder: sorter.field === 'total_payed' && sorter.order,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            )
        },
        {
            title: t('payments.remaining'),
            dataIndex: 'total_debit',
            key: 'total_debit',
            width: 150,
            sorter: (a, b) => a.children_debit - b.children_debit,
            sortOrder: sorter.field === 'children_debit' && sorter.order,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            )
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortOrder: sorter.field === 'status' && sorter.order,
            render: (text) => (
                <StatusBadge tone={statusTones[text] ?? 'muted'}>{getStatusName(text)}</StatusBadge>
            ),
        },
        {
            title: t('admissions.payment'),
            key: 'payment',
            width: 128,
            fixed: 'right',
            render: (_, record) => (
                <ActionButton
                    icon={<HiCreditCard size="18"/>}
                    collapseOnMobile={false}
                    className="!h-8 !px-2.5 sm:!px-2.5 !text-xs"
                    disabled={typeof record.children_debit === 'number' && record.children_debit === 0 ? 'disabled-row' : ''}
                    onClick={() => handlePaymentClick(record)}
                >
                    {t('debtors.pay')}
                </ActionButton>
            ),
        },
    ]
    ;

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B] truncate">
                        {record.patient_name}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">{record.doctor_name}</span>
                </div>
                <RowIconButton
                    onClick={() => handlePaymentClick(record)}
                    disabled={typeof record.children_debit === 'number' && record.children_debit === 0 ? 'disabled-row' : ''}
                    aria-label={t('debtors.pay')}
                >
                    <HiCreditCard size="18"/>
                </RowIconButton>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <StatusBadge tone={statusTones[record.status] ?? 'muted'}>
                    {getStatusName(record.status)}
                </StatusBadge>
                <span className="tabular-nums">{sum(record.total_amount)}</span>
                <span className="tabular-nums">{sum(record.total_debit)}</span>
            </div>

            {record.services && (
                <p className="mt-1.5 mb-0 text-xs text-slate-500 line-clamp-2">{record.services}</p>
            )}
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('admissions.reappointments')}
                    count={pagination.total}
                    filters={
                        <Input.Search
                            placeholder={t('common.searchName')}
                            allowClear
                            size="small"
                            enterButton={t('payments.searchBtn')}
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="md:!w-[260px]"
                        />
                    }
                />

                <Spin spinning={loading}>
                    <DataTable
                        columns={columns}
                        dataSource={filteredData}
                        mobileCard={renderMobileCard}
                        scrollX={1480}
                        pagination={{
                            ...pagination,
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                        }}
                        onChange={handleTableChange}
                        rowKey="key"
                        rowClassName={(record) => (typeof record.children_debit === 'number' && record.children_debit === 0 ? 'disabled-row' : '')}
                    />
                </Spin>
            </PageCard>

            <Modal
                centered
                title={paymentReceipt ? t('admissions.printReceipt') : t('admissions.paymentInfo')}
                visible={isModalVisible}
                onCancel={handleModalClose}
                width="100%"
                style={{ maxWidth: 520 }}
                footer={[
                    <Button key="back" onClick={handleModalClose}>
                        {t('common.back')}
                    </Button>,
                    paymentReceipt ? (
                        <Button key="print" type="primary" onClick={() => window.print()}>
                            {t('admissions.printReceipt')}
                        </Button>
                    ) : (
                        <Button key="submit" type="primary" onClick={handlePayment}>
                            {t('debtors.pay')}
                        </Button>
                    )
                ]}
            >
                {paymentReceipt ? (
                    <div className="w-full max-w-sm mx-auto bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
                        {/* Header with Shop Logo and Name */}
                        <div className="text-center mb-4">
                            <img src="/logo.svg" alt="Shop Logo" className="h-10 mx-auto mb-2"/>
                            <Typography.Title level={5} style={{fontWeight: 'bold'}} className="uppercase">Geolink Clinic</Typography.Title>
                            <Typography.Text className="text-slate-500">ул. Мустақиллик, 123, г. Бухара</Typography.Text>
                        </div>

                        <Divider dashed className="border-slate-200"/>

                        {/* Services or Products */}
                        <div className="mb-3 text-sm text-slate-700">
                            {selectedVisit && selectedVisit.orders && selectedVisit.orders.service && (
                                selectedVisit.orders.service.name
                            )}
                        </div>

                        <hr className="border-slate-200"/>

                        {/* Summary */}
                        <div className="mb-2 mt-2 text-slate-700">
                            <div className="flex justify-between gap-3">
                                <Typography.Text strong>{t('payments.total')}:</Typography.Text>
                                <Typography.Text className="tabular-nums">{sum(paymentReceipt.children_amount)}</Typography.Text>
                            </div>
                            <div className="flex justify-between gap-3">
                                <Typography.Text strong>{t('payments.paid')}:</Typography.Text>
                                <Typography.Text className="tabular-nums">{sum(selectedVisit.children_payed)}</Typography.Text>
                            </div>
                            <div className="flex justify-between gap-3">
                                <Typography.Text strong>{t('payments.remaining')}:</Typography.Text>
                                <Typography.Text className="tabular-nums">{sum(selectedVisit.children_debit)}</Typography.Text>
                            </div>
                        </div>

                        <Divider dashed className="border-slate-200"/>

                        {/* Payment Information */}
                        <div className="mb-4 text-slate-700">
                            <div className="flex justify-between gap-3">
                                <Typography.Text>{t('payments.paymentMethod')}:</Typography.Text>
                                <Typography.Text>{paymentTypeNames[paymentReceipt.type]}</Typography.Text>
                            </div>
                            <div className="flex justify-between gap-3">
                                <Typography.Text>{t('payments.paymentStatus')}:</Typography.Text>
                                <Typography.Text>{paymentBillNames[paymentReceipt.bill]}</Typography.Text>
                            </div>
                        </div>

                        <Divider dashed className="border-slate-200"/>
                        <img src="/qr.svg" alt="" className="mx-auto mt-2" width="140" height="140"/>

                        {/* Footer */}
                        <div className="text-center">
                            <Typography.Text className="text-slate-500">{t('payments.thankYou')}</Typography.Text>
                            <br/>
                            <Typography.Text className="text-slate-500">Тел: +998 33 135 21 01</Typography.Text>
                            <br/>
                            <Typography.Text className="text-slate-500">front.geolink.uz</Typography.Text>
                        </div>
                    </div>
                ) : selectedVisit ? (
                    <div>
                        <Typography.Title level={4}>{t('payments.paymentSlip')}</Typography.Title>
                        <p className="text-sm text-slate-700"><strong>{t('payments.mainService')}:</strong> {selectedVisit.orders[0]?.service?.name}</p>
                        <p className="text-sm text-slate-700"><strong>{t('nav.services')}:</strong>
                            {selectedVisit && selectedVisit.orders && selectedVisit.orders.length > 0 ? (
                                selectedVisit.orders.map((order, index) => (
                                    <span key={order.id}>
                                        {index > 0 && ', '}
                                        {order.service.name} x{order.count}
                                    </span>
                                ))
                            ) : (
                                t('payments.noServiceData')
                            )}
                        </p>
                        <p className="text-sm text-slate-700 tabular-nums"><strong>{t('payments.total')}:</strong> {sum(selectedVisit.total_amount)}</p>
                        <p className="text-sm text-slate-700 tabular-nums"><strong>{t('payments.paid')}:</strong> {sum(selectedVisit.total_payed)}</p>
                        <Input
                            className='mb-5'
                            type="number"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            placeholder={t('payments.enterAmount')}
                            style={{ marginTop: 10 }}
                        />
                        <Radio.Group onChange={(e) => setPaymentType(e.target.value)} value={paymentType}>
                            <Radio value="cash">{t('payments.cash')}</Radio>
                            {/* <Radio value="credit">Қарз</Radio> */}
                            <Radio value="card">{t('payments.creditCard')}</Radio>
                        </Radio.Group>
                        <Divider/>
                        <p className="text-sm text-slate-700 tabular-nums"><strong>{t('payments.paymentAmount')}:</strong> {sum(selectedVisit.children_debit)}</p>
                    </div>
                ) : (
                    <p>{t('payments.noOrderData')}</p>
                )}
            </Modal>
        </PageShell>
    );
};

export default NewAdmissions;
